// Daily statistics series as the cabinet returns them (feature 013):
// - a snapshot metric (`views`, `likes`, `members`…) is the running total of the day,
//   its day-by-day change is `<metric>Change`;
// - a flow metric (follower gains, page views) already belongs to its day;
// - a value that is not stored is left out of the point, so series are aligned by date
//   and a missing value becomes a gap (null), never a zero.

export type StatDailyPoint = { date?: string } & { [key: string]: number | string | null | undefined };

export const CHANGE_SUFFIX = 'Change';

export const CHANNEL_FLOW_KEYS = ['followerGainOrganic', 'followerGainPaid', 'pageViews', 'uniquePageViews'];

export interface AlignedSeries {
    key: string;
    data: Array<number | null>;
}

export interface AlignedDailySeries {
    labels: string[];
    series: AlignedSeries[];
}

/** The `<metric>Change` keys present in the points, in first-seen order. */
export const changeKeys = (items: StatDailyPoint[] | null | undefined): string[] => {
    const keys: string[] = [];
    (items || []).forEach((item) => {
        Object.keys(item).forEach((key) => {
            if (key.length > CHANGE_SUFFIX.length && key.endsWith(CHANGE_SUFFIX) && !keys.includes(key)) {
                keys.push(key);
            }
        });
    });
    return keys;
};

/** Every metric key present in the points except `date` and the `<metric>Change` keys. */
export const valueKeys = (items: StatDailyPoint[] | null | undefined, exclude: string[] = []): string[] => {
    const keys: string[] = [];
    (items || []).forEach((item) => {
        Object.keys(item).forEach((key) => {
            if (key === 'date' || key.endsWith(CHANGE_SUFFIX) || exclude.includes(key) || keys.includes(key)) {
                return;
            }
            keys.push(key);
        });
    });
    return keys;
};

/** The metric a `<metric>Change` key belongs to (`viewsChange` → `views`). */
export const baseMetric = (key: string): string =>
    key.endsWith(CHANGE_SUFFIX) ? key.slice(0, -CHANGE_SUFFIX.length) : key;

const isNumber = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value);

/**
 * One label per date (sorted) and, for each key, one value per label: the stored value
 * or null when that day has none. A key with no value on any day is dropped.
 */
export const alignDailySeries = (items: StatDailyPoint[] | null | undefined, keys: string[]): AlignedDailySeries => {
    const byDate = new Map<string, StatDailyPoint>();
    (items || []).forEach((item) => {
        if (item && typeof item.date === 'string') {
            byDate.set(item.date, {...(byDate.get(item.date) || {}), ...item});
        }
    });
    const labels = Array.from(byDate.keys()).sort();

    const series = keys
        .map((key) => ({
            key,
            data: labels.map((date) => {
                const value = byDate.get(date)[key];
                return isNumber(value) ? value : null;
            }),
        }))
        .filter((serie) => serie.data.some((value) => value !== null));

    return {labels, series};
};

/** Whether any point holds a value for one of the keys. */
export const hasAnyValue = (items: StatDailyPoint[] | null | undefined, keys: string[]): boolean =>
    (items || []).some((item) => keys.some((key) => isNumber(item[key])));
